/**
 * appStore.ts
 * Global application state: active room, current slide, and Office readiness.
 */

import { create } from 'zustand';
import type { Room } from '@types-local/room.types';
import type { ActivityConfig } from '@types-local/quiz.types';
import type { OfficeViewType } from '@types-local/office.types';
import type { Tier } from '@db/firebase';

interface AppState {
  // Office.js readiness
  isOfficeReady: boolean;
  setOfficeReady: (ready: boolean) => void;

  // Current view mode
  viewMode: OfficeViewType;
  setViewMode: (mode: OfficeViewType) => void;

  // Active presentation room
  activeRoom: Room | null;
  setActiveRoom: (room: Room | null) => void;

  // Active class ID (selected before presenting)
  activeClassId: string | null;
  setActiveClassId: (id: string | null) => void;

  // Current slide config (injected activity for active slide)
  currentSlideConfig: ActivityConfig | null;
  setCurrentSlideConfig: (config: ActivityConfig | null) => void;

  // Current slide index (1-based)
  currentSlideIndex: number;
  setCurrentSlideIndex: (index: number) => void;

  // Teacher Firebase UID
  teacherUid: string | null;
  setTeacherUid: (uid: string | null) => void;

  // Teacher subscription tier (from the main app)
  tier: Tier;
  setTier: (tier: Tier) => void;

  // Presenter mode overlay visibility
  presenterOverlayVisible: boolean;
  setPresenterOverlayVisible: (visible: boolean) => void;
}

export const useAppStore = create<AppState>((set) => ({
  isOfficeReady: false,
  setOfficeReady: (ready) => set({ isOfficeReady: ready }),

  viewMode: 'edit',
  setViewMode: (mode) => set({ viewMode: mode }),

  activeRoom: null,
  setActiveRoom: (room) => set({ activeRoom: room }),

  activeClassId: null,
  setActiveClassId: (id) => set({ activeClassId: id }),

  currentSlideConfig: null,
  setCurrentSlideConfig: (config) => set({ currentSlideConfig: config }),

  currentSlideIndex: 1,
  setCurrentSlideIndex: (index) => set({ currentSlideIndex: index }),

  teacherUid: null,
  setTeacherUid: (uid) => set({ teacherUid: uid }),

  tier: 'free',
  setTier: (tier) => set({ tier }),

  presenterOverlayVisible: false,
  setPresenterOverlayVisible: (visible) => set({ presenterOverlayVisible: visible }),
}));
